import React, { Component } from "react";
import Video from "twilio-video";
import MPSBarLoader from "../MPSBarLoader";
import VideoRoom from "./VideoRoom";
import GoToHome from "./GoToHome";
import FloatingVideoCallEnded from "./FloatingVideoCallEnded";
import CallTranscription from "./CallTranscription";
import userService from "../../services/userService";
import FeaturesService from "../../services/featuresService";
import { store } from "../../State/store/configureStore";
import { devLog } from "../../utils/devLog";
import {
  installVideoCallDiagnostics,
  reportVideoCallFailure,
} from "../../services/videoCallDiagnostics";
import { Button } from "reactstrap";
import { secondsToHourMinSecs } from "../../utils/utils";
import Text from "../Text";
import Messages from "../../Shared/Messages";
import "./VideoComponent.scss";

const CALL_ERROR =
  "Unable to connect to the video call. Please try again later or contact us if the issue persists.";

function getErrorMessage(error) {
  if (error.code === 53103) {
    return "Video call is not scheduled or ended already.";
  }

  return CALL_ERROR;
}

export default class VideoComponent extends Component {
  constructor(props) {
    super(props);
    this.activeRoom = null;
    this.previewTracks = null;
    this.state = {};
  }
  componentDidUpdate(prevProps, prevState) {
    // If we just got an activeRoom and localVideo is true, try to attach local tracks
    if (
      this.state.activeRoom &&
      !prevState.activeRoom &&
      this.state.localVideo &&
      this.localMedia
    ) {
      if (!this.localMedia.querySelector("video")) {
        this.attachParticipantTracks(
          this.state.activeRoom.localParticipant,
          this.localMedia
        );
      }
    }
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.leaveRoomIfJoined);
    this.makeVideoCall();
  }

  makeVideoCall = () => {
    let { roomName, token, participants } = this.props;
    this.setState({ loading: true, participants });

    let connectOptions = {
      name: roomName,
      logLevel: "debug",
    };

    if (this.previewTracks) {
      connectOptions.tracks = this.previewTracks;
    }

    // Enhanced-connection mode: force ICE to only gather relay (TURN)
    // candidates. On locked-down networks this skips the direct/srflx
    // attempts that otherwise consume the SDK's 10s ICE timer.
    if (this.props.useRelay) {
      connectOptions.iceTransportPolicy = "relay";
    }

    Video.connect(token, connectOptions).then(this.roomJoined, (error) => {
      // Report before showing the error. Diagnostics otherwise only attach in
      // roomJoined, so a call that never connects — the case actually worth
      // diagnosing — reported nothing at all.
      const failUser = store.getState().user && store.getState().user.data;
      reportVideoCallFailure(
        {
          roomName: this.props.roomName,
          role: this.props.patientId ? "STAFF" : "PATIENT",
          userId:
            failUser && failUser.loggedInUser && failUser.loggedInUser.id,
          facilityId:
            failUser &&
            failUser.loggedInUser &&
            failUser.loggedInUser.facilityId,
        },
        error,
        "twilioConnect"
      );
      this.setState({
        loading: false,
        error: getErrorMessage(error),
      });
    });
  };

  attachTracks = (tracks, container) => {
    tracks.forEach((track) => {
      if (track.attach) {
        container.appendChild(track.attach());
      } else if (track.track) {
        container.appendChild(track.track.attach());
      }
    });
  };

  attachParticipantTracks = (participant, container, remote) => {
    if (!container) return; // Add this line
    let tracks = Array.from(participant.tracks.values());
    this.attachTracks(tracks, container);
    this.setVideoStyles(container);
  };

  setVideoStyles = (container) => {
    if (!container) return; // Add this line
    let video = container.querySelector("video");
    if (video) {
      video.style.height = "100%";
      video.style.width = "100%";
      video.style.objectFit = "contain";
    }
  };

  detachTracks = (tracks) => {
    tracks.forEach((track) => {
      if (track.detach) {
        track.detach().forEach((detachedElement) => {
          detachedElement.remove();
        });
      } else if (track.track) {
        track.track.detach().forEach((detachedElement) => {
          detachedElement.remove();
        });
      }
    });
  };

  detachParticipantTracks = (participant) => {
    let tracks = Array.from(participant.tracks.values());
    this.detachTracks(tracks);
  };

  watchRemoteVideoEnabled = (participant) => {
    participant.on("trackDisabled", (publication) => {
      if (publication.kind === "video") {
        this.setState({ remoteVideoOff: true });
      }
    });
    participant.on("trackEnabled", (publication) => {
      if (publication.kind === "video") {
        this.setState({ remoteVideoOff: false });
      }
    });
    participant.videoTracks.forEach((publication) => {
      if (publication.isTrackEnabled === false) {
        this.setState({ remoteVideoOff: true });
      }
    });
  };

  startTranscription = (room) => {
    if (!this.props.embedded || !this.props.patientId) return;
    if (!FeaturesService.canGenerateCallSummaries()) return;
    const userData = store.getState().user && store.getState().user.data;
    this.transcription = new CallTranscription({
      staffLabel:
        userData && userData.loggedInUser && userData.loggedInUser.text,
      patientLabel: this.props.otherParticipantName,
    });
    this.transcription
      .start()
      .then(() => this.setState({ transcribing: true }))
      .catch((err) => {
        devLog("[CALL TRANSCRIPTION] unavailable for this facility", err);
        this.transcription = null;
      });

    room.participants.forEach((participant) => {
      participant.audioTracks.forEach((publication) => {
        if (publication.track && this.transcription) {
          this.transcription.addRemoteAudioTrack(
            publication.track.mediaStreamTrack
          );
        }
      });
    });
  };

  setSummaryState = (state) => {
    if (!this._unmounted) {
      this.setState(state);
    }
  };

  flushTranscription = () => {
    const transcription = this.transcription;
    if (!transcription) return;
    this.transcription = null;
    transcription.stop();
    const transcript = transcription.getTranscript();
    if (!transcript || !this.props.patientId || !this.props.roomName) {
      this.setSummaryState({ transcribing: false });
      return;
    }
    this.setSummaryState({ transcribing: false, summaryStatus: "generating" });
    userService
      .saveVideoCallTranscript(
        this.props.patientId,
        this.props.roomName,
        transcript
      )
      .then((response) => {
        const summary = response && response.summary;
        if (summary && this.props.onSummaryReady) {
          // Hand the summary to the full-screen review modal (also closes the
          // widget). Deliberately not gated on _unmounted — the modal should
          // still open if the user closed the widget while generating.
          this.props.onSummaryReady(summary);
          this.setSummaryState({ summaryStatus: null });
          return;
        }
        // No summary (missing AI creds, LLM failure, …) fails silently —
        // the transcript is still stored and attached to the comms record.
        this.setSummaryState(
          summary
            ? { summaryStatus: "ready", summaryText: summary }
            : { summaryStatus: null }
        );
      })
      .catch((err) => {
        devLog("[CALL TRANSCRIPTION] save failed", err);
        this.setSummaryState({ summaryStatus: null });
      });
  };

  roomJoined = (room) => {
    this.activeRoom = room;
    this.setState({ activeRoom: room, loading: false });
    window.room = room.name;
    this.setState({
      activeRoom: room,
      loading: false,
      localVideo: true,
    });
    const userData = store.getState().user && store.getState().user.data;
    this.diagnostics = installVideoCallDiagnostics(room, {
      roomName: this.props.roomName || room.name,
      role: this.props.patientId ? "STAFF" : "PATIENT",
      userId: userData && userData.loggedInUser && userData.loggedInUser.id,
      facilityId:
        userData && userData.loggedInUser && userData.loggedInUser.facilityId,
    });
    let previewContainer = this.localMedia;
    if (previewContainer && !previewContainer.querySelector("video")) {
      this.attachParticipantTracks(room.localParticipant, previewContainer);
    }

    this.startTranscription(room);

    room.participants.forEach((participant) => {
      let previewContainer = this.remoteMedia;
      this.attachParticipantTracks(participant, previewContainer, true);
      this.watchRemoteVideoEnabled(participant);
    });

    room.on("participantConnected", (participant) => {
      this.watchRemoteVideoEnabled(participant);
    });

    room.on("trackSubscribed", (track, participant) => {
      // Remembers that the far end actually reached us. Used to decide whether
      // offering "Try enhanced connection" makes sense when the call ends.
      this.remoteMediaSeen = true;
      let previewContainer = this.remoteMedia;
      this.setState({ remoteVideo: true });
      this.setVideoStyles(previewContainer);
      this.attachTracks([track], previewContainer);
      if (track.kind === "audio" && this.transcription) {
        this.transcription.addRemoteAudioTrack(track.mediaStreamTrack);
      }
    });

    room.on("trackUnsubscribed", (track, participant) => {
      this.setState({ remoteVideo: false });
      this.detachTracks([track]);
    });

    room.on("participantDisconnected", (participant) => {
      this.detachParticipantTracks(participant);
      this.leaveRoom();
    });

    room.on("disconnected", () => {
      if (this.previewTracks) {
        this.previewTracks.forEach((track) => {
          track.stop();
        });
      }
      this.detachParticipantTracks(room.localParticipant);
      room.participants.forEach(this.detachParticipantTracks);
      this.leaveRoom();
    });
  };

  leaveRoomIfJoined = () => {
    this.flushTranscription();
    if (this.activeRoom) {
      this.activeRoom.disconnect();
      this.activeRoom = null;
      this.setState({
        remoteVideo: false,
        activeRoom: null,
        callEnded: true,
      });
    }
    if (this.diagnostics) {
      this.diagnostics.flush();
      this.diagnostics = null;
    }
  };

  setCallDuration = (callDuration) => {
    if (!this.state.callEnded) {
      this.setState({ callDuration });
    }
  };

  componentWillUnmount() {
    this._unmounted = true;
    this.leaveRoomIfJoined();
  }

  leaveRoom = () => {
    this.leaveRoomIfJoined();
  };

  setLocalMediaRef = (ref) => {
    this.localMedia = ref;
  };

  setRemoteMediaRef = (ref) => {
    this.remoteMedia = ref;
  };

  render() {
    let { loading, error, callEnded, activeRoom } = this.state;

    if (loading) {
      return (
        <div style={{ margin: "100px auto", width: 100 }}>
          <MPSBarLoader />
        </div>
      );
    }

    if (error) {
      return (
        <div className="d-flex justify-content-center align-items-center my-4 flex-column">
          <p className="text-danger">{error}</p>
          {this.props.onTryEnhancedConnection && !this.remoteMediaSeen && (
            <div className="video-component__enhance">
              <p className="video-component__enhance-prompt">
                <Text content={Messages.video_call_connection_trouble} />
              </p>
              <Button
                color="primary"
                onClick={() => this.props.onTryEnhancedConnection()}
              >
                <Text content={Messages.video_call_try_enhanced_connection} />
              </Button>
              <p className="video-component__enhance-hint">
                <Text content={Messages.video_call_enhanced_connection_hint} />
              </p>
            </div>
          )}
        </div>
      );
    }

    if (callEnded) {
      if (this.props.embedded) {
        return (
          <FloatingVideoCallEnded
            callDuration={this.state.callDuration}
            summaryStatus={this.state.summaryStatus}
            onClose={
              this.props.onClose ||
              (() => {
                window.close();
              })
            }
            onTryEnhancedConnection={
              this.remoteMediaSeen
                ? undefined
                : this.props.onTryEnhancedConnection
            }
          />
        );
      }
      return (
        <div className="d-flex justify-content-center align-items-center my-4 flex-column">
          <p className="font-lg">Call has ended. You both joined. </p>
          <p className="font-lg">
            Total call duration: {secondsToHourMinSecs(this.state.callDuration)}
          </p>

          {this.props.onTryEnhancedConnection && (
            <div className="video-component__enhance">
              <p className="video-component__enhance-prompt">
                <Text content={Messages.video_call_connection_trouble} />
              </p>
              <Button
                color="primary"
                onClick={() => this.props.onTryEnhancedConnection()}
              >
                <Text content={Messages.video_call_try_enhanced_connection} />
              </Button>
              <p className="video-component__enhance-hint">
                <Text content={Messages.video_call_enhanced_connection_hint} />
              </p>
            </div>
          )}

          {this.props.newRoom ? (
            <div className="my-3">
              <Button
                className="btn btn-secondary"
                onClick={() => {
                  if (this.props.onClose) {
                    this.props.onClose();
                  } else {
                    window.close();
                  }
                }}
              >
                Close
              </Button>
            </div>
          ) : (
            <div className="my-3">
              <GoToHome />
            </div>
          )}
        </div>
      );
    }

    if (activeRoom) {
      return (
        <div
          className={
            this.props.embedded ? "floating-video-call__video-fill" : undefined
          }
        >
          {this.state.activeRoom && (
            <VideoRoom
              activeRoom={this.state.activeRoom}
              setRemoteMedia={this.setRemoteMediaRef}
              setLocalMedia={this.setLocalMediaRef}
              remoteVideo={this.state.remoteVideo}
              leaveRoom={this.leaveRoom}
              existing={this.props.existing}
              otherParticipantName={this.props.otherParticipantName}
              remoteVideoOff={this.state.remoteVideoOff}
              localParticipantName={
                store.getState().user?.data?.loggedInUser?.text
              }
              localVideo={this.state.localVideo}
              setCallDuration={this.setCallDuration}
              callEnded={callEnded}
              embedded={this.props.embedded}
              transcribing={this.state.transcribing}
            />
          )}
        </div>
      );
    }

    return null;
  }
}
